import { RFPSearch } from "api/Api";
import PaginatedTableView from "components/common/containers/PaginatedTableView";
import { useMemo, useRef, useState } from "react";
import NavigationView from "components/common/NavigationView";
import RFPListItemView from "components/rfps/RFPListItemView";
import usePaginatedData from "hooks/usePaginatedData";
import { useApiClient } from "providers/ApiClientProvider";
import Overlay from "components/common/containers/overlays/Overlay";
import FullRFPView from "components/rfps/FullRFPView";
import DeprecatedButton from "components/common/DeprecatedButton";
import RFPSearchMenu from "components/rfps/search-menu/RFPSearchMenu";
import Columns from "components/common/containers/Columns";
import {
  ShortListsProvider,
  useShortLists,
} from "providers/ShortListsProvider";
import { copyToClipboard } from "lib/clipboard";
import Rows from "components/common/containers/Rows";
import { cn } from "lib/utils";
import ConnectionLostPill from "components/common/ConnecionLostPill";

const RFPsRoute = () => {
  const apiClient = useApiClient();
  const [rfps, , paginatedData] = usePaginatedData({
    endpoint: apiClient.rfp.rfpRfpSearchList,
    mode: "pages",
    map: (data) => data,
    deps: [],
  });
  const [viewingRFP, setViewingRFP] = useState<RFPSearch | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerPinned, setHeaderPinned] = useState(false);

  const viewDetails = (rfp: RFPSearch) => {
    setViewingRFP(rfp);
  };

  const copyLink = (rfp: RFPSearch) => {
    copyToClipboard(`${window.location.origin}/rfps/${rfp.id}/`);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!headerRef.current) return;
    const target = e.target as HTMLElement;
    setHeaderPinned(target.scrollTop > headerRef.current.clientHeight);
  };

  return (
    <ShortListsProvider>
      <NavigationView
        selected="rfps"
        fullWidth={true}
        scrollable={false}
        padContent={false}
      >
        <Columns className="flex gap-md justify-center grow">
          <div
            className="grow overflow-y-scroll pl-[18px]"
            onScroll={handleScroll}
          >
            <Rows
              className="shrink-0 max-w-[950px] mx-auto pt-lg pb-md"
              ref={headerRef}
            >
              <h1 className="text-2xl font-semibold w-full">RFPs</h1>
              <Columns className="gap-sm">
                <p className="text-secondary grow">
                  Discover RFPs and shortlist them for later.
                </p>
                {!!paginatedData.count && (
                  <div className="px-sm text-secondary flex items-center select-none shrink-0">
                    {paginatedData.count} RFPs found
                  </div>
                )}
              </Columns>
            </Rows>
            <RFPSearchMenu
              className={cn("max-w-[950px] mx-auto w-full sticky top-0")}
              isPinnedToTop={headerPinned}
              resultCount={paginatedData.count}
              onUpdated={() => {
                // Refresh the data
                paginatedData.refresh();
              }}
            />
            <PaginatedTableView
              results={rfps}
              paginatedData={paginatedData}
              columns={[]}
              className="pt-xs"
              internalScroll={false}
              scrollableClassName="border-0"
              renderRow={(rfp, Cell, Row) => (
                <Row key={rfp.id}>
                  <Cell className="px-none max-w-[950px] mx-auto">
                    <RFPListItemView
                      rfp={rfp}
                      onViewDetails={() => viewDetails(rfp)}
                      intent="shortlist"
                    />
                  </Cell>
                </Row>
              )}
            />
          </div>
        </Columns>
        {viewingRFP && (
          <Overlay
            open={true}
            title={viewingRFP.display_name}
            extraTitleBarElements={
              <DeprecatedButton
                icon="link-simple"
                className="ml-sm grow-0"
                onClick={() => copyLink(viewingRFP)}
              />
            }
            variant="full-screen-bordered"
            onClose={() => setViewingRFP(null)}
          >
            <FullRFPView rfp={viewingRFP} intent="shortlist" />
          </Overlay>
        )}
        <RFPsConnectionLostPill />
      </NavigationView>
    </ShortListsProvider>
  );
};

const RFPsConnectionLostPill = () => {
  const { connectionStatus, refreshConnection } = useShortLists();
  return useMemo(() => {
    switch (connectionStatus) {
      case "reconnecting":
      case "closed":
        return (
          <div className="absolute bottom-sm left-0 right-0 flex justify-center">
            <ConnectionLostPill reconnect={refreshConnection} />
          </div>
        );
      default:
        return null;
    }
  }, [connectionStatus, refreshConnection]);
};

export default RFPsRoute;
